/**
 * Learning loop service (PROD-572).
 *
 * Implements ADR-001 correction 2 + HC8 no-over-suppression: a clinician
 * override reduces an NBA's confidence and re-buckets its priority but
 * never deletes or permanently suppresses it. The reduction is bounded
 * by `MIN_NBA_CONFIDENCE` so the candidate remains surfaceable.
 *
 * Public surface:
 *   - `applyOverrideToNba(payload, candidate)` - pure deterministic
 *     transition function. Returns a new `NbaCandidate` with reduced
 *     confidence, recomputed priority, `state = 'reduced'`, incremented
 *     overrideCount, and lastOverrideAt updated. Emits a
 *     `nba_confidence_adjusted` telemetry event. NEVER deletes or
 *     suppresses.
 *   - `emitLabBasketConfirmationRequired(basket)` - emits a single
 *     `lab_basket_confirmation_required` event when the basket renders
 *     with pre-selected items pending confirmation. The view layer
 *     calls this on first render; it carries the LOINC-shaped codes of
 *     the pending items.
 *
 * The decay formula is documented in
 * `docs/learning-loop-confidence-decay.md`:
 *   `next = max(MIN_NBA_CONFIDENCE, current * CONFIDENCE_DECAY_FACTOR)`
 * where `CONFIDENCE_DECAY_FACTOR = 0.6` and `MIN_NBA_CONFIDENCE = 0.20`.
 *
 * Determinism (HC4): the function is pure with no I/O beyond the
 * telemetry emit; identical (payload, candidate) inputs produce
 * identical outputs.
 *
 * HC5: zero em / en dashes anywhere.
 */
import { emitTelemetry } from './telemetry';
import {
  decayConfidence,
  MIN_NBA_CONFIDENCE,
  priorityForConfidence,
  nbaCandidateSchema,
  type NbaCandidate,
} from '../types/nba';
import { pendingConfirmationCodes } from '../data/synthetic-pathway-states';
import type { LabBasket } from '../types/lab-basket';
import type { OverrideEvent } from '../types/override';

/**
 * Subset of the `OverrideEvent` payload that the learning loop needs.
 * Accepting a structural subset (rather than the full event) keeps the
 * contract narrow and makes the function trivially testable.
 */
export interface OverrideAdjustmentPayload {
  pathway_id: string;
  step_id: string;
  nba_id: string;
  tenant_id: string;
  reason_category: OverrideEvent['reason_category'];
  correlation_id: string;
  ts?: string;
}

/**
 * Telemetry shape emitted on every confidence adjustment. The view layer
 * (PROD-573 TelemetryEventStream) renders these as
 * `nba_confidence_adjusted` rows. Tests assert against this exact shape.
 */
export interface NbaConfidenceAdjustedTelemetry {
  pathway_id: string;
  step_id: string;
  nba_id: string;
  tenant_id: string;
  reason_category: OverrideEvent['reason_category'];
  previous_confidence: number;
  next_confidence: number;
  previous_priority: NbaCandidate['priority'];
  next_priority: NbaCandidate['priority'];
  previous_state: NbaCandidate['state'];
  next_state: NbaCandidate['state'];
  override_count: number;
  floored: boolean;
  correlation_id: string;
  ts: string;
}

/**
 * Apply a clinician override event to an NBA candidate. Returns a new
 * candidate with:
 *   - `confidence` reduced via `decayConfidence` (floored at
 *     `MIN_NBA_CONFIDENCE`)
 *   - `priority` recomputed from the new confidence
 *   - `state` set to `'reduced'` (never `'suppressed'` - HC8 forbids it)
 *   - `overrideCount` incremented
 *   - `lastOverrideAt` set to the override timestamp (or now)
 *
 * Always emits a `nba_confidence_adjusted` telemetry event.
 *
 * Throws if the payload's `nba_id` does not match the candidate's `id`,
 * to prevent silent cross-NBA bleed.
 */
export function applyOverrideToNba(
  payload: OverrideAdjustmentPayload,
  candidate: NbaCandidate,
): NbaCandidate {
  if (payload.nba_id !== candidate.id) {
    throw new Error(
      `applyOverrideToNba: payload.nba_id (${payload.nba_id}) does not match candidate.id (${candidate.id})`,
    );
  }
  if (payload.tenant_id !== candidate.tenantId) {
    throw new Error(
      `applyOverrideToNba: payload.tenant_id (${payload.tenant_id}) does not match candidate.tenantId (${candidate.tenantId}); HC6 multi-tenant isolation`,
    );
  }

  const previousConfidence = candidate.confidence;
  const previousPriority = candidate.priority;
  const previousState = candidate.state;

  const nextConfidence = decayConfidence(previousConfidence);
  const nextPriority = priorityForConfidence(nextConfidence);
  // ADR-001 + HC8: NBA never enters a `suppressed` state. The lifecycle
  // marker becomes `reduced` after any override; admins restore via a
  // separate path (Phase 3) which sets `'restored'`.
  const nextState: NbaCandidate['state'] = 'reduced';
  const ts = payload.ts ?? new Date().toISOString();
  const floored = nextConfidence === MIN_NBA_CONFIDENCE;

  const next = nbaCandidateSchema.parse({
    ...candidate,
    confidence: nextConfidence,
    priority: nextPriority,
    state: nextState,
    overrideCount: candidate.overrideCount + 1,
    lastOverrideAt: ts,
  });

  const event: NbaConfidenceAdjustedTelemetry = {
    pathway_id: payload.pathway_id,
    step_id: payload.step_id,
    nba_id: payload.nba_id,
    tenant_id: payload.tenant_id,
    reason_category: payload.reason_category,
    previous_confidence: previousConfidence,
    next_confidence: nextConfidence,
    previous_priority: previousPriority,
    next_priority: nextPriority,
    previous_state: previousState,
    next_state: nextState,
    override_count: next.overrideCount,
    floored,
    correlation_id: payload.correlation_id,
    ts,
  };

  // HC9 telemetry-real: the test asserts this exact emit through
  // vi.mock on `../services/telemetry`.
  emitTelemetry('nba_confidence_adjusted', {
    ...event,
  });

  return next;
}

/**
 * Emit `lab_basket_confirmation_required` when a basket renders with
 * pre-selected items pending confirmation. Returns the codes that
 * triggered the emit (so the view can also surface them inline).
 *
 * No emit happens when the basket has no items requiring confirmation;
 * silence is correct in that case.
 */
export function emitLabBasketConfirmationRequired(basket: LabBasket): string[] {
  const codes = pendingConfirmationCodes(basket);
  if (codes.length === 0) {
    return [];
  }
  emitTelemetry('lab_basket_confirmation_required', {
    pathway_id: basket.pathwayId,
    step_id: basket.stepId,
    tenant_id: basket.tenantId,
    pending_codes: codes,
    pending_count: codes.length,
    ts: new Date().toISOString(),
  });
  return codes;
}
